// Discord-parity "Create your server" template-picker wizard.
//
// Mitchell ask 2026-04-25: replace the plain name-only modal with a 3-step
// flow keyed off the new `/api/templates/*` endpoints (handlers/templates.rs).
//
// Step 1 — group grid (business / personal / family / support / community
//          / gaming) + "Custom (Blank)" + a grayed-out "Coming Soon"
//          community-templates section at the foot.
// Step 2 — sub-templates inside the picked group, with a channel preview
//          (count + 2-3 example names).
// Step 3 — name + visibility, then POST /api/servers/from-template
//          (or /api/servers/add when the user picked Custom Blank).
//
// State is held in a closure — re-renders reuse the same modal frame so the
// header/footer/animation stays consistent. No global mutation.

import { showModal, closeModal, modalFrame } from './modal';
import { showToast } from './toast';
import { validateRequired, clearAllErrors } from './form-validation';
import {
  apiTemplateGroups,
  apiTemplatesByGroup,
  apiCreateServerFromTemplate,
  post,
} from '../api';
import type {
  TemplateGroup,
  ServerTemplate,
  TemplateChannel,
  CreatedServerLite,
} from '../api';
import {
  iconBriefcase,
  iconUsers,
  iconHome,
  iconChevronRight,
} from '../icons';
import type { Person } from '../types';

export interface CreateServerWizardOptions {
  /** Current user — used as owner_id on the new server. May be empty if
   *  the caller hasn't bootstrapped a person row yet; the wizard will
   *  lazily create one before submit. */
  ownerId: string;
  /** Fired after the new server is provisioned. Caller is responsible for
   *  refreshing the server rail and (optionally) navigating into it. */
  onCreated: (serverId: string) => void;
}

type Visibility = 'private' | 'public';

interface WizardState {
  step: 1 | 2 | 3;
  groups: TemplateGroup[] | null;
  selectedGroup: TemplateGroup | null;
  templatesForGroup: ServerTemplate[] | null;
  /** Set when user picks a sub-template. null = Custom (blank). */
  selectedTemplate: ServerTemplate | null;
  /** Custom-blank route. When true, step 3 submits to /api/servers/add. */
  customBlank: boolean;
  /** Server-create-in-flight guard. */
  submitting: boolean;
  /** Last typed name + visibility — preserved across step transitions. */
  draftName: string;
  draftVisibility: Visibility;
  /** Owner id (resolved lazily — see ensureOwnerId). */
  ownerId: string;
  /** Server-creation gating mode (D3). Loaded on wizard open. */
  creationMode: 'open' | 'invite_only' | 'disabled';
  /** Creation code entered by the user (needed when mode = invite_only). */
  draftCreationCode: string;
}

const GROUP_ICONS: Record<string, () => string> = {
  business:  iconBriefcase,
  personal:  () => userOutlineSvg(),
  family:    iconHome,
  support:   () => heartSvg(),
  community: iconUsers,
  gaming:    () => gamepadSvg(),
};

function userOutlineSvg(): string {
  return `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`;
}
function heartSvg(): string {
  return `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>`;
}
function gamepadSvg(): string {
  return `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="6" y1="11" x2="10" y2="11"/><line x1="8" y1="9" x2="8" y2="13"/><line x1="15" y1="12" x2="15.01" y2="12"/><line x1="18" y1="10" x2="18.01" y2="10"/><path d="M17.32 5H6.68a4 4 0 0 0-3.978 3.59l-1.5 12A4 4 0 0 0 5.18 25h13.64a4 4 0 0 0 3.978-4.41l-1.5-12A4 4 0 0 0 17.32 5z"/></svg>`;
}
function blankSvg(): string {
  return `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>`;
}

function groupIcon(gid: string): string {
  const fn = GROUP_ICONS[gid];
  return fn ? fn() : userOutlineSvg();
}

let activeWizard: WizardState | null = null;
let activeOpts: CreateServerWizardOptions | null = null;

export function openCreateServerWizard(opts: CreateServerWizardOptions): void {
  activeOpts = opts;
  activeWizard = {
    step: 1,
    groups: null,
    selectedGroup: null,
    templatesForGroup: null,
    selectedTemplate: null,
    customBlank: false,
    submitting: false,
    draftName: '',
    draftVisibility: 'private',
    ownerId: opts.ownerId,
    creationMode: 'open',
    draftCreationCode: '',
  };
  renderWizard();
  void loadGroups();
  // Load creation mode (D3)
  void loadCreationMode();
}

function renderWizard(): void {
  const w = activeWizard;
  if (!w) return;

  if (w.step === 1) {
    showModal(modalFrame(
      'Create Your Server',
      step1Body(w),
      `<button class="btn btn-secondary" id="cs-cancel" type="button">Cancel</button>`
    ));
    wireCommonChrome();
    wireStep1();
    return;
  }

  if (w.step === 2) {
    const groupLabel = w.selectedGroup?.label ?? '';
    showModal(modalFrame(
      `Pick a ${esc(groupLabel)} template`,
      step2Body(w),
      `<button class="btn btn-secondary" id="cs-back" type="button">Back</button>
       <button class="btn btn-secondary" id="cs-cancel" type="button">Cancel</button>`
    ));
    wireCommonChrome();
    wireStep2();
    return;
  }

  // Step 3
  const titleSuffix = w.customBlank
    ? 'Custom server'
    : (w.selectedTemplate?.label ?? 'Server');
  showModal(modalFrame(
    `Customize — ${esc(titleSuffix)}`,
    step3Body(w),
    `<button class="btn btn-secondary" id="cs-back" type="button">Back</button>
     <button class="btn btn-primary" id="cs-submit" type="button">${w.submitting ? 'Creating\u2026' : 'Create'}</button>`
  ));
  wireCommonChrome();
  wireStep3();
}

// ── Step 1: group grid ──────────────────────────────────────────────────

function step1Body(w: WizardState): string {
  const intro = `
    <div class="cs-wizard-intro">
      <div class="cs-wizard-headline">Your server is where you and your circle hang out.</div>
      <div class="cs-wizard-sub">Start from a template — channels and roles come pre-wired. You can change anything later.</div>
    </div>
  `;

  let grid = '<div class="cs-wizard-grid" role="list" aria-label="Server templates">';

  if (w.groups === null) {
    for (let i = 0; i < 6; i++) {
      grid += `<div class="cs-wizard-tile cs-wizard-tile-skel" aria-hidden="true"></div>`;
    }
  } else {
    for (const g of w.groups) {
      grid += `
        <button type="button" class="cs-wizard-tile" role="listitem"
                data-group-id="${escAttr(g.id)}" aria-label="${escAttr(g.label)} \u2014 ${escAttr(g.description)}">
          <span class="cs-wizard-tile-icon">${groupIcon(g.id)}</span>
          <span class="cs-wizard-tile-label">${esc(g.label)}</span>
          <span class="cs-wizard-tile-sub">${esc(g.description)}</span>
        </button>`;
    }
    // Custom (blank) tile — always last in the same grid.
    grid += `
      <button type="button" class="cs-wizard-tile cs-wizard-tile-blank" role="listitem"
              data-blank="1" aria-label="Custom \u2014 start from a blank server">
        <span class="cs-wizard-tile-icon">${blankSvg()}</span>
        <span class="cs-wizard-tile-label">Custom (Blank)</span>
        <span class="cs-wizard-tile-sub">Start fresh \u2014 just a #general channel.</span>
      </button>`;
  }
  grid += '</div>';

  const comingSoon = `
    <div class="cs-wizard-section cs-wizard-soon">
      <div class="cs-wizard-section-title">Community templates</div>
      <div class="cs-wizard-soon-card" aria-disabled="true" role="note">
        <div class="cs-wizard-soon-pill">Coming Soon</div>
        <div class="cs-wizard-soon-text">Submit your own templates so others can spin up servers like yours.</div>
      </div>
    </div>
  `;

  return `${intro}${grid}${comingSoon}`;
}

function wireStep1(): void {
  const root = document.getElementById('modal');
  if (!root) return;
  root.querySelectorAll<HTMLElement>('[data-group-id]').forEach(el => {
    el.addEventListener('click', () => {
      const w = activeWizard;
      if (!w || !w.groups) return;
      const gid = el.getAttribute('data-group-id') || '';
      const group = w.groups.find(g => g.id === gid) || null;
      if (!group) return;
      w.selectedGroup = group;
      w.templatesForGroup = null;
      w.selectedTemplate = null;
      w.customBlank = false;
      w.step = 2;
      renderWizard();
      void loadTemplatesForGroup(group.id);
    });
  });
  root.querySelector<HTMLElement>('[data-blank="1"]')?.addEventListener('click', () => {
    const w = activeWizard;
    if (!w) return;
    w.customBlank = true;
    w.selectedTemplate = null;
    w.selectedGroup = null;
    w.step = 3;
    renderWizard();
  });
}

// ── Step 2: sub-template list ───────────────────────────────────────────

function step2Body(w: WizardState): string {
  if (w.templatesForGroup === null) {
    return `
      <div class="cs-wizard-list" aria-busy="true">
        <div class="cs-wizard-row cs-wizard-row-skel"></div>
        <div class="cs-wizard-row cs-wizard-row-skel"></div>
        <div class="cs-wizard-row cs-wizard-row-skel"></div>
      </div>`;
  }

  if (w.templatesForGroup.length === 0) {
    return `<div class="cs-wizard-empty">No templates in this group yet.</div>`;
  }

  const rows = w.templatesForGroup.map(t => {
    const preview = previewChannels(t);
    const count = preview.total;
    const examples = preview.examples;
    const exampleStr = examples.length
      ? examples.map(n => `#${n}`).join(', ')
      : 'no preset channels';
    const more = count > examples.length ? ` <span class="cs-wizard-more">+${count - examples.length} more</span>` : '';
    return `
      <button type="button" class="cs-wizard-row" data-template-id="${escAttr(t.id)}"
              aria-label="${escAttr(t.label)} \u2014 ${escAttr(t.description)}">
        <span class="cs-wizard-row-body">
          <span class="cs-wizard-row-title">${esc(t.label)}</span>
          <span class="cs-wizard-row-desc">${esc(t.description)}</span>
          <span class="cs-wizard-row-meta">${count} channel${count === 1 ? '' : 's'} \u00b7 ${esc(exampleStr)}${more}</span>
        </span>
        <span class="cs-wizard-row-chevron" aria-hidden="true">${iconChevronRight()}</span>
      </button>`;
  }).join('');

  return `<div class="cs-wizard-list">${rows}</div>`;
}

function wireStep2(): void {
  const root = document.getElementById('modal');
  if (!root) return;
  root.querySelectorAll<HTMLElement>('[data-template-id]').forEach(el => {
    el.addEventListener('click', () => {
      const w = activeWizard;
      if (!w || !w.templatesForGroup) return;
      const tid = el.getAttribute('data-template-id') || '';
      const tpl = w.templatesForGroup.find(t => t.id === tid) || null;
      if (!tpl) return;
      w.selectedTemplate = tpl;
      w.customBlank = false;
      // Seed the visibility from the template's default but still let
      // the user override it on step 3 (Discord parity — public/private).
      w.draftVisibility = (tpl.visibility === 'public') ? 'public' : 'private';
      w.step = 3;
      renderWizard();
    });
  });
  document.getElementById('cs-back')?.addEventListener('click', () => {
    const w = activeWizard;
    if (!w) return;
    w.step = 1;
    renderWizard();
  });
}

// ── Step 3: name + visibility ───────────────────────────────────────────

function step3Body(w: WizardState): string {
  const name = w.draftName;
  const vis = w.draftVisibility;
  const summary = w.customBlank
    ? `<div class="cs-wizard-summary">
         <div class="cs-wizard-summary-title">Custom (Blank)</div>
         <div class="cs-wizard-summary-desc">A clean server with just a #general channel.</div>
       </div>`
    : (w.selectedTemplate ? `
         <div class="cs-wizard-summary">
           <div class="cs-wizard-summary-title">${esc(w.selectedTemplate.label)}</div>
           <div class="cs-wizard-summary-desc">${esc(w.selectedTemplate.description)}</div>
         </div>` : '');

  // Server-creation gating (D3): show code field when mode = invite_only
  const codeField = w.creationMode === 'invite_only' ? `
    <div class="form-group">
      <label class="form-label" for="cs-creation-code">Creation Code</label>
      <input class="form-input" id="cs-creation-code" maxlength="12" autocomplete="off"
             placeholder="S-XXXXXXXX" value="${escAttr(w.draftCreationCode)}"
             style="font-family:monospace;letter-spacing:0.05em;text-transform:uppercase" />
      <div class="form-hint" style="font-size:12px;color:var(--fg-muted);margin-top:4px">Enter the server creation code you received.</div>
    </div>` : '';

  return `
    ${summary}
    <div class="form-group">
      <label class="form-label" for="cs-server-name">Server name</label>
      <input class="form-input" id="cs-server-name" maxlength="100" autocomplete="off"
             placeholder="My team" value="${escAttr(name)}" />
    </div>
    ${codeField}
    <div class="form-group">
      <span class="form-label">Visibility</span>
      <div class="cs-wizard-vis" role="radiogroup" aria-label="Server visibility">
        <label class="cs-wizard-vis-opt ${vis === 'private' ? 'is-selected' : ''}">
          <input type="radio" name="cs-vis" value="private" ${vis === 'private' ? 'checked' : ''} />
          <span class="cs-wizard-vis-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          </span>
          <span class="cs-wizard-vis-body">
            <span class="cs-wizard-vis-title">Private</span>
            <span class="cs-wizard-vis-sub">Invite-only. Won\u2019t appear in Discover.</span>
          </span>
        </label>
        <label class="cs-wizard-vis-opt ${vis === 'public' ? 'is-selected' : ''}">
          <input type="radio" name="cs-vis" value="public" ${vis === 'public' ? 'checked' : ''} />
          <span class="cs-wizard-vis-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
          </span>
          <span class="cs-wizard-vis-body">
            <span class="cs-wizard-vis-title">Public</span>
            <span class="cs-wizard-vis-sub">Listed in Discover. Anyone can request to join.</span>
          </span>
        </label>
      </div>
    </div>
  `;
}

function wireStep3(): void {
  const w = activeWizard;
  if (!w) return;

  const nameEl = document.getElementById('cs-server-name') as HTMLInputElement | null;
  if (nameEl) {
    nameEl.addEventListener('input', () => {
      const cur = activeWizard;
      if (!cur) return;
      cur.draftName = nameEl.value;
    });
  }

  // Creation code input (D3)
  const codeEl = document.getElementById('cs-creation-code') as HTMLInputElement | null;
  if (codeEl) {
    codeEl.addEventListener('input', () => {
      const cur = activeWizard;
      if (!cur) return;
      cur.draftCreationCode = codeEl.value;
    });
  }

  document.querySelectorAll<HTMLInputElement>('input[name="cs-vis"]').forEach(el => {
    el.addEventListener('change', () => {
      if (!el.checked) return;
      const cur = activeWizard;
      if (!cur) return;
      cur.draftVisibility = el.value === 'public' ? 'public' : 'private';
      // Re-render to update the .is-selected highlight without losing focus
      // on the radio (focus management here is fine — radios re-attach).
      renderWizard();
    });
  });

  document.getElementById('cs-back')?.addEventListener('click', () => {
    const cur = activeWizard;
    if (!cur) return;
    cur.step = cur.customBlank ? 1 : 2;
    renderWizard();
  });

  document.getElementById('cs-submit')?.addEventListener('click', () => {
    void submitCreate();
  });
}

// ── Data loading ────────────────────────────────────────────────────────

async function loadCreationMode(): Promise<void> {
  const w = activeWizard;
  if (!w) return;
  try {
    const { get: apiGet } = await import('../api');
    const res = await apiGet<{ mode: string }>('/api/server-creation-mode');
    if (activeWizard !== w) return;
    const m = res.mode;
    if (m === 'invite_only' || m === 'disabled') {
      w.creationMode = m as 'invite_only' | 'disabled';
      // If disabled, show toast and close wizard
      if (m === 'disabled') {
        closeModal();
        activeWizard = null;
        activeOpts = null;
        showToast('Server creation is currently disabled by the owner', 'err');
      }
    }
  } catch { /* default to open */ }
}

async function loadGroups(): Promise<void> {
  const w = activeWizard;
  if (!w) return;
  try {
    const groups = await apiTemplateGroups();
    if (activeWizard !== w) return; // wizard closed/replaced while loading
    w.groups = groups;
    if (w.step === 1) renderWizard();
  } catch {
    if (activeWizard !== w) return;
    w.groups = [];
    if (w.step === 1) renderWizard();
    showToast('Failed to load templates', 'err');
  }
}

async function loadTemplatesForGroup(gid: string): Promise<void> {
  const w = activeWizard;
  if (!w) return;
  try {
    const list = await apiTemplatesByGroup(gid);
    if (activeWizard !== w) return;
    if (w.selectedGroup?.id !== gid) return;
    w.templatesForGroup = list;
    if (w.step === 2) renderWizard();
  } catch {
    if (activeWizard !== w) return;
    w.templatesForGroup = [];
    if (w.step === 2) renderWizard();
    showToast('Failed to load templates', 'err');
  }
}

// ── Submit ──────────────────────────────────────────────────────────────

async function submitCreate(): Promise<void> {
  const w = activeWizard;
  const opts = activeOpts;
  if (!w || !opts || w.submitting) return;

  clearAllErrors();
  if (!validateRequired('cs-server-name', 'Server name')) return;

  const name = (document.getElementById('cs-server-name') as HTMLInputElement).value.trim();
  w.draftName = name;

  w.submitting = true;
  renderWizard();

  try {
    const ownerId = await ensureOwnerId(w.ownerId);
    w.ownerId = ownerId;

    let created: CreatedServerLite;
    // Include creation_code when mode = invite_only (D3)
    const creationCode = w.creationMode === 'invite_only' ? w.draftCreationCode.trim() : undefined;
    if (w.customBlank || !w.selectedTemplate) {
      // Custom-blank path uses the legacy /api/servers/add — same shape as
      // the previous modal so we don't need a new backend endpoint.
      const body: Record<string, string> = {
        name,
        type: 'community',
        icon: '',
        description: '',
        visibility: w.draftVisibility,
        owner_id: ownerId,
      };
      if (creationCode) body['creation_code'] = creationCode;
      created = await post<CreatedServerLite>('/api/servers/add', body);
    } else {
      created = await apiCreateServerFromTemplate(
        w.selectedTemplate.id,
        name,
        ownerId,
        w.draftVisibility,
      );
    }

    closeModal();
    activeWizard = null;
    activeOpts = null;
    showToast('Server created');
    opts.onCreated(created.id);
  } catch (e) {
    w.submitting = false;
    renderWizard();
    const msg = (e instanceof Error && e.message) ? e.message : 'Failed to create server';
    showToast(msg, 'err');
  }
}

async function ensureOwnerId(current: string): Promise<string> {
  if (current) return current;
  // Mirrors the legacy modal\u2019s lazy person-row provisioning: when a user
  // hasn\u2019t set up identity yet (very early bootstrap), create a stub
  // person row and use its id as owner_id.
  const person = await post<Person>('/api/people/add', { name: 'You', role: 'Owner' });
  return person.id;
}

// ── Helpers ─────────────────────────────────────────────────────────────

function previewChannels(t: ServerTemplate): { total: number; examples: string[] } {
  try {
    const parsed = JSON.parse(t.channels_json) as TemplateChannel[];
    if (!Array.isArray(parsed)) return { total: 0, examples: [] };
    const names = parsed.map(c => c.name).filter((n): n is string => typeof n === 'string' && n.length > 0);
    return { total: names.length, examples: names.slice(0, 3) };
  } catch {
    return { total: 0, examples: [] };
  }
}

function wireCommonChrome(): void {
  document.getElementById('modal-close')?.addEventListener('click', cancelWizard);
  document.getElementById('cs-cancel')?.addEventListener('click', cancelWizard);
}

function cancelWizard(): void {
  closeModal();
  activeWizard = null;
  activeOpts = null;
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function escAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
